import React from 'react';
import { Checkbox, FormControlLabel, Paper, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as Cesium from 'cesium';
import { Viewer, ImageryLayer, Cesium3DTileset } from 'resium';
import { useMap, useErrorHandler, usePageMenuDrawer, PAGE_MENU_DRAWER_WIDTH, useMonitorMap } from '@wheeebo/polaris-ui';
import MapLegend from '@wheeebo/polaris-ui/src/map/google/MapLegend';
import CesiumContainer from './CesiumContainer';
import GeoFenceEntity from './GeoFenceEntity';
import NoFlyZoneEntity from './NoFlyZoneEntity';
import SafePortEntity from './SafePortEntity';
const CESIUM_TARGET_FRAME_RATE = 12;
const CESIUM_IMAGERY_SENTINEL_2 = 2;
const CESIUM_3D_TILES_OSM_BUILDING = 96188;
const FALLBACK_CENTER = { lat: 35.465, lng: 139.76 };
function isLocal(env) {
    if (!env) {
        return false;
    }
    return !['DEV', 'STG', 'PROD'].includes(env);
}
function nearlySameLatLngDegrees(aLat, aLng, bLat, bLng, eps = 1e-6) {
    return Math.abs(aLat - bLat) < eps && Math.abs(aLng - bLng) < eps;
}
function CesiumMap({ env, realtime, mission, geoFences: geoFences_, noFlyZones, safePorts: safePorts_, waypoints, footprints, handoffCameraAltitudeMeters, infoBoxLeftPadding, children, onSelectedVehicleIdChanged, }) {
    const theme = useTheme();
    const localEnv = React.useMemo(() => isLocal(env), [env]);
    const { showGeoFence, showSafePort, showNoFlyZone } = useMonitorMap();
    const ref = React.useRef(null);
    const drawer = usePageMenuDrawer();
    const { showError } = useErrorHandler();
    const { center, setCenter } = useMap();
    const [showBuilding, setShowBUilding] = React.useState(false);
    const [geoFences, setGeoFences] = React.useState();
    const [safePorts, setSafePorts] = React.useState();
    const [terrainProvider, setTerrainProvider] = React.useState();
    const [imageryProvider, setImageryProvider] = React.useState();
    const tileSet = React.useMemo(() => Cesium.IonResource.fromAssetId(CESIUM_3D_TILES_OSM_BUILDING), []);
    /** When false, avoid instant camera snaps that fight the 2D→3D handoff flyTo. */
    const allowCenterSnapRef = React.useRef(true);
    React.useLayoutEffect(() => {
        allowCenterSnapRef.current = handoffCameraAltitudeMeters == null;
    }, [handoffCameraAltitudeMeters]);
    const moveMapCenter = React.useCallback((lat, lng, dontOverwrite) => {
        const viewer = ref.current?.cesiumElement;
        if (!viewer) {
            return;
        }
        viewer.camera.flyTo({ destination: Cesium.Cartesian3.fromDegrees(lng, lat, 1000), duration: 0 });
        if (!dontOverwrite) {
            setCenter?.({ lat, lng });
        }
    }, [setCenter]);
    React.useEffect(() => {
        let mounted = true;
        const loadProviders = async () => {
            try {
                const provider = await Cesium.createWorldTerrainAsync();
                if (mounted) {
                    setTerrainProvider(provider);
                }
            }
            catch (error) {
                console.error('Failed to load Cesium terrain provider', error);
            }
            if (!localEnv) {
                if (mounted) {
                    setImageryProvider(undefined);
                }
                return;
            }
            try {
                const provider = await Cesium.IonImageryProvider.fromAssetId(CESIUM_IMAGERY_SENTINEL_2);
                if (mounted) {
                    setImageryProvider(provider);
                }
            }
            catch (error) {
                console.error('Failed to load Cesium imagery provider', error);
                if (mounted) {
                    setImageryProvider(undefined);
                }
            }
        };
        void loadProviders();
        return () => {
            mounted = false;
        };
    }, [localEnv]);
    React.useEffect(() => {
        const viewer = ref.current?.cesiumElement;
        if (!viewer) {
            return;
        }
        const oldKeys = new Set(geoFences?.map((e) => e.id));
        const newKeys = new Set(geoFences_?.map((e) => e.id));
        [...oldKeys].filter((e) => !newKeys.has(e))?.forEach((e) => viewer.entities.removeById(e));
        setGeoFences(geoFences_);
    }, [geoFences, geoFences_]);
    React.useEffect(() => {
        const viewer = ref.current?.cesiumElement;
        if (!viewer) {
            return;
        }
        const oldKeys = new Set(safePorts?.map((e) => e.id));
        const newKeys = new Set(safePorts_?.map((e) => e.id));
        [...oldKeys].filter((e) => !newKeys.has(e))?.forEach((e) => viewer.entities.removeById(e));
        setSafePorts(safePorts_);
    }, [safePorts, safePorts_]);
    React.useEffect(() => {
        if (!center) {
            return;
        }
        if (!allowCenterSnapRef.current) {
            return;
        }
        let raf = 0;
        const run = () => {
            const viewer = ref.current?.cesiumElement;
            if (!viewer) {
                raf = requestAnimationFrame(run);
                return;
            }
            const cameraCenter = viewer.camera.positionCartographic;
            const camLat = Cesium.Math.toDegrees(cameraCenter.latitude);
            const camLng = Cesium.Math.toDegrees(cameraCenter.longitude);
            if (!nearlySameLatLngDegrees(camLat, camLng, center.lat, center.lng)) {
                moveMapCenter(center.lat, center.lng, true);
            }
        };
        run();
        return () => cancelAnimationFrame(raf);
    }, [center, moveMapCenter]);
    React.useEffect(() => {
        if (handoffCameraAltitudeMeters == null || !center) {
            return;
        }
        let cancelled = false;
        let raf = 0;
        const flyWhenReady = () => {
            if (cancelled) {
                return;
            }
            const viewer = ref.current?.cesiumElement;
            if (!viewer) {
                raf = requestAnimationFrame(flyWhenReady);
                return;
            }
            allowCenterSnapRef.current = false;
            viewer.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(center.lng, center.lat, handoffCameraAltitudeMeters),
                duration: 1.2,
                complete: () => {
                    allowCenterSnapRef.current = true;
                },
            });
        };
        flyWhenReady();
        return () => {
            cancelled = true;
            cancelAnimationFrame(raf);
        };
    }, [center, handoffCameraAltitudeMeters]);
    React.useEffect(() => {
        const viewer = ref.current?.cesiumElement;
        if (!viewer) {
            return;
        }
        viewer.geocoder.viewModel.keepExpanded = true;
        viewer.trackedEntityChanged.addEventListener((entity) => {
            console.log(entity?.id);
            onSelectedVehicleIdChanged?.(entity?.id);
        });
    }, [onSelectedVehicleIdChanged]);
    React.useEffect(() => {
        const viewer = ref.current?.cesiumElement;
        if (!viewer) {
            return;
        }
        viewer.scene.light = new Cesium.DirectionalLight({ direction: viewer.scene.camera.directionWC });
        viewer.scene.preRender.addEventListener(() => {
            const light = viewer.scene.light;
            light.direction = Cesium.Cartesian3.clone(viewer.scene.camera.directionWC, light.direction);
        });
        const unsubscribe = viewer.camera.moveEnd.addEventListener(() => {
            const position = viewer.camera.positionCartographic;
            setCenter?.({ lat: Cesium.Math.toDegrees(position.latitude), lng: Cesium.Math.toDegrees(position.longitude) });
        });
        return () => unsubscribe();
    }, [setCenter]);
    React.useEffect(() => {
        if (center || !setCenter) {
            return;
        }
        const waypoint = waypoints?.at(0)?.location;
        if (waypoint) {
            console.debug('center waypoint');
            moveMapCenter(waypoint.lat, waypoint.lng);
            return;
        }
        const safePort = safePorts?.at(0)?.location;
        if (safePort) {
            console.debug('center safe port');
            moveMapCenter(safePort.lat, safePort.lng);
            return;
        }
        const geoFence = geoFences?.at(0)?.area?.at(0)?.at(0);
        if (geoFence) {
            console.debug('center geo fence');
            moveMapCenter(geoFence[1], geoFence[0]);
            return;
        }
        const footprint = footprints?.at(0)?.path?.[0];
        if (footprint) {
            console.debug('center footprint');
            moveMapCenter(footprint.lat, footprint.lng);
            return;
        }
        if (!navigator.geolocation) {
            moveMapCenter(FALLBACK_CENTER.lat, FALLBACK_CENTER.lng, true);
            return;
        }
        // Explicit settle timer: a permission prompt the user ignores calls
        // neither callback, which previously left the globe on the whole-earth
        // view forever. After 4 s we take the fixed fallback.
        let cancelled = false;
        const settleTimer = window.setTimeout(() => {
            if (!cancelled) {
                moveMapCenter(FALLBACK_CENTER.lat, FALLBACK_CENTER.lng, true);
            }
        }, 4000);
        navigator.geolocation.getCurrentPosition((position) => {
            if (cancelled) {
                return;
            }
            window.clearTimeout(settleTimer);
            console.log('center geo location');
            moveMapCenter(position.coords.latitude, position.coords.longitude);
        }, () => {
            if (cancelled) {
                return;
            }
            window.clearTimeout(settleTimer);
            showError(new Error('Unable to retrieve your location'));
            moveMapCenter(FALLBACK_CENTER.lat, FALLBACK_CENTER.lng, true);
        }, { maximumAge: Infinity, enableHighAccuracy: true, timeout: 10000 });
        return () => {
            cancelled = true;
            window.clearTimeout(settleTimer);
        };
    }, [center, moveMapCenter, waypoints, geoFences, safePorts, noFlyZones, footprints, showError, setCenter]);
    return (React.createElement(CesiumContainer, { isDrawerOpen: drawer.isOpen, infoBoxLeftPadding: infoBoxLeftPadding },
        React.createElement(Viewer, { full: true, ref: ref, terrainProvider: terrainProvider, targetFrameRate: CESIUM_TARGET_FRAME_RATE, shouldAnimate: realtime, fullscreenButton: false, homeButton: false, sceneModePicker: false, baseLayerPicker: false, navigationHelpButton: false, projectionPicker: false, vrButton: false, animation: !realtime, timeline: !realtime },
            localEnv && imageryProvider ? React.createElement(ImageryLayer, { imageryProvider: imageryProvider }) : null,
            showBuilding ? React.createElement(Cesium3DTileset, { url: tileSet }) : null,
            showGeoFence
                ? geoFences?.map((e) => (React.createElement(GeoFenceEntity, { key: e.id, geoFence: e, mission: mission })))
                : null,
            showSafePort
                ? safePorts?.map((e) => (React.createElement(SafePortEntity, { key: e.id, safePort: e, mission: mission })))
                : null,
            showNoFlyZone
                ? noFlyZones?.map((e) => (React.createElement(NoFlyZoneEntity, { key: e.id, noFlyZone: e })))
                : null,
            children,
            React.createElement(Stack, { gap: 1 / 2, sx: {
                    position: 'absolute',
                    left: (drawer.isOpen ? PAGE_MENU_DRAWER_WIDTH : parseInt(theme.spacing(7))) + 4,
                    bottom: realtime ? 4 : 114,
                } },
                React.createElement(Paper, { variant: "outlined", sx: { p: 3 / 4 } },
                    React.createElement(FormControlLabel, { slotProps: { typography: { fontSize: '0.75rem' } }, control: React.createElement(Checkbox, { size: "small", checked: showBuilding, onChange: () => setShowBUilding((e) => !e), sx: { pl: 3 / 2, pr: 1 / 2 } }), label: "3D Building" })),
                React.createElement(MapLegend, { enableRadar: !!realtime, interactive: true })))));
}
export default React.memo(CesiumMap);
